import React, { FC, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { Box, Grid, Stack, Typography } from "@mui/material";

import { Button as GradientButton, IconButton, NavLink } from "@ui/index";
import { Text } from "@ui/typography";

import { useIsBigDesktop } from "@hooks/layout";
import { useSubitems } from "@hooks/useSubitems";

import { QuestionFlowDialog } from "@components/QuestionFlowDialog";
import { contactNavItems, freeUseNavItems, licensingNavItems } from "@layouts/constants";
import { getSubitems } from "@layouts/helpers";
import { DropdownNavItem } from "@layouts/types";

import { FavoritesHeartIcon, GeneralLogo, LicenseTestShield, ShopIcon } from "@/assets/svg";
import { StyledDivider } from "@/components/components";
import { useAuth } from "@/hooks/auth";
import AccountMenu from "@/layouts/components/AccountMenu";
import { LinkWrapper, SecondHeaderContainer, SubItem } from "@/layouts/components/components";
import { useAppSelector } from "@/store/hooks";
import { getFavoritesState } from "@/store/selectors/getFavorites";
import { ERoutes } from "@/utils/constants";

import { DesktopDropdown } from "./DesktopDropdown";

type TSecondHeader = {
  handleOpenDialog: (isLogin: boolean) => void;
};

const SecondHeader: FC<TSecondHeader> = ({ handleOpenDialog }) => {
  const { t } = useTranslation("navigation");

  const navigate = useNavigate();

  const { isLoggedIn, isLoading } = useAuth();

  const { subitems, itemRefs, underlineRef } = useSubitems(getSubitems);
  const [openQuestionDialog, setOpenQuestionDialog] = useState(false);

  const isBigDesktop = useIsBigDesktop();

  const favoritesState = useAppSelector(getFavoritesState);

  const activePopperHandler = useRef<() => void | null>(null);

  const handleCookieClock = () => {
    const w = window as any;
    w.cookieconsent.openPreferencesCenter();
  };

  const renderSubitems = ({ route, key, icon, target }: DropdownNavItem, i: number) => (
    <SubItem
      key={route}
      ref={(el) => (itemRefs.current[i] = el as HTMLDivElement)}
      sx={{
        "&& .active p":
          ((route !== ERoutes.Test && location.search.includes(ERoutes.Test)) || route !== ERoutes.Test) &&
          key !== "Cookies"
            ? {
                color: "#fff",
                svg: { fill: "#fff" },
              }
            : {},
        "&& a": {
          textDecoration: "none",
          textUnderlineOffset: "unset",
        },
      }}
    >
      <NavLink
        noLineAnimation={true}
        to={route}
        target={target}
        end
        className={
          route === ERoutes.Catalog && location.pathname.includes(ERoutes.CatalogLandingFreeBackground) ? "active" : ""
        }
        onClick={(e) => {
          if (key === "Cookies") {
            e.preventDefault();
            handleCookieClock();
          }
        }}
      >
        {!location.pathname.includes(ERoutes.Deals) && icon && icon}

        <Text isFullWidth size={1} sx={{ "&&": { color: "#F5DFC4" } }}>
          {t(key)}
        </Text>
        {location.pathname.includes(ERoutes.Deals) && icon && icon}
      </NavLink>
    </SubItem>
  );

  return (
    <>
      <SecondHeaderContainer>
        <Grid
          container
          justifyContent={"space-between"}
          direction="row"
          alignItems="center"
          margin="0 auto"
          height="100%"
          width="100%"
          maxWidth={"xl"}
          paddingX={{ xs: "16px", md: "40px", lg: "250px" }}
          minHeight="95px"
        >
          <Grid item xs={2.5} lg="auto" md="auto" sm="auto" sx={{ transform: "translateY(-2px)" }}>
            <Link to={ERoutes.Home}>
              <Stack height="32px">
                <GeneralLogo style={{ height: "100%", width: "100%" }} />
              </Stack>
            </Link>
          </Grid>

          <Grid item maxWidth="70%">
            <Stack
              direction="row"
              gap={{ xs: "32px", md: isLoggedIn ? "32px" : "22px", lg: "32px" }}
              alignItems="center"
              justifyContent="center"
            >
              <LinkWrapper>
                <NavLink noLineAnimation={true} to={ERoutes.Catalog}>
                  {t("music")}
                </NavLink>
              </LinkWrapper>

              <DesktopDropdown
                title={t("freeUse")}
                navItems={freeUseNavItems}
                activePopperHandler={activePopperHandler}
              />
              <DesktopDropdown
                title={t("licensing")}
                navItems={licensingNavItems}
                activePopperHandler={activePopperHandler}
              />
              <DesktopDropdown
                title={t("contacts")}
                navItems={contactNavItems}
                activePopperHandler={activePopperHandler}
              />
              <LinkWrapper>
                <NavLink noLineAnimation={true} to={ERoutes.Deals} end>
                  {t("deals")}
                </NavLink>
              </LinkWrapper>
              <LinkWrapper>
                <NavLink noLineAnimation={true} to={ERoutes.Shop} target="_blank">
                  <Stack
                    sx={{
                      alignItems: "center",
                      flexDirection: "row",
                      gap: "4px",
                    }}
                  >
                    {t("shop")}
                    <ShopIcon />
                  </Stack>
                </NavLink>
              </LinkWrapper>
            </Stack>
          </Grid>

          <Grid item md="auto">
            <Stack width="100%" alignItems="end" justifyContent="center">
              {isLoggedIn && !isLoading ? (
                <Stack
                  direction="row"
                  width="100%"
                  spacing="16px"
                  alignItems="center"
                  justifyContent="flex-end"
                  sx={{
                    minWidth: "fit-content",
                  }}
                >
                  <IconButton
                    isTransparent
                    onClick={() => navigate(ERoutes.Favorites)}
                    sx={{
                      "&&&": {
                        width: "40px",
                        height: "40px",
                        border: "1px solid #ad8d75",
                        backgroundColor: "#453426",
                        borderRadius: "50%",
                        alignItems: "center",
                        justifyContent: "center",
                        "&:hover": {
                          borderColor: "#ffdca8",
                          backgroundColor: "#5C412A",
                        },
                      },
                    }}
                  >
                    <Stack sx={{ position: "relative" }}>
                      <FavoritesHeartIcon width="22px" height="22px" />
                    </Stack>

                    <Stack
                      sx={{
                        position: "absolute",
                        top: "-5px",
                        right: "-10px",
                      }}
                    >
                      <Box sx={{ backgroundColor: "#FFDCA8", borderRadius: "50%", width: "18px", height: "18px" }}>
                        <Typography
                          sx={{
                            fontFamily: "Kaisei Opti",
                            fontSize: "0.875rem",
                            lineHeight: "1.1rem",
                            fontWeight: "500",
                            textAlign: "center",
                            color: "#453425",
                          }}
                        >
                          {favoritesState.length}
                        </Typography>
                      </Box>
                    </Stack>
                  </IconButton>

                  <AccountMenu />
                </Stack>
              ) : (
                <Stack direction="row" gap={{ md: isLoggedIn ? "16px" : "8px", lg: "16px" }}>
                  <GradientButton
                    width={isBigDesktop ? 125 : undefined}
                    isOutlined
                    onClick={() => handleOpenDialog(true)}
                  >
                    {t("logIn")}
                  </GradientButton>
                  <GradientButton width={isBigDesktop ? 134 : undefined} onClick={() => handleOpenDialog(false)}>
                    {t("signUp")}
                  </GradientButton>
                </Stack>
              )}
            </Stack>
          </Grid>
        </Grid>
        <StyledDivider $isHeader sx={{ "&&": { position: "relative" } }} />
        {subitems && subitems.length ? (
          <Box sx={{ background: "rgb(127 99 78)", position: "absolute", zIndex: 5, width: "100%" }}>
            <Box
              display={"flex"}
              justifyContent={"space-between"}
              alignItems={"center"}
              width={"100%"}
              maxWidth={"xl"}
              margin={"0 auto"}
              paddingX={{ xs: "16px", md: "40px", lg: "250px" }}
            >
              <Box display={"flex"} gap={"32px"}>
                {subitems.map(renderSubitems)}
                <Box ref={underlineRef} className="nav-underline"></Box>
              </Box>
              <Box
                display={"flex"}
                gap={"10px"}
                alignItems={"center"}
                onClick={() => setOpenQuestionDialog(true)}
                sx={{
                  svg: {
                    width: 20,
                    height: 22,
                  },
                  cursor: "pointer",
                  padding: " 16px 0px",
                  "&&&:hover p": {
                    color: "rgb(255, 220, 168)",
                    transition: "color 0.3s ease-in-out",
                  },
                }}
              >
                <LicenseTestShield />

                <Text
                  fontWeight={400}
                  isFullWidth
                  size={1}
                  sx={{
                    "&&&": {
                      color: "rgb(233 233 233)",
                    },
                  }}
                >
                  {t("freeUseTestHeader")}
                </Text>
              </Box>
            </Box>
          </Box>
        ) : null}
      </SecondHeaderContainer>
      <QuestionFlowDialog open={openQuestionDialog} onClose={() => setOpenQuestionDialog(false)} />
    </>
  );
};

export default SecondHeader;
